import useFetchProjectData from '../api/project_data/useFetchProjectData.ts';
import {useQuery, useMutation} from 'react-query';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
    EntityDataModel,
    EntityDataOperation,
    EntityDataPayload,
    EntityManipulationMethods,
    UnknownObject
} from '../types.ts';
import useProjectDataHub, {
    ProjectDataHubMessages,
    useHubMessageListener
} from './useProjectDataHub.ts';
import createEntityManipulationMethods from './createEntityManipulationMethods.ts';
import {ProjectDataIndex} from '../api/types.ts';
import {HubConnectionState} from '@microsoft/signalr';
import isLargeObject from '../lib/isLargeObject.ts';
import useMergeLargeObject from '../api/project_data/useMergeLargeObject.ts';
import {useAuthStateManager} from '../hooks/useAuthStateManager.tsx';
import useClientConfig from '../hooks/useClientConfig.tsx';
import getLargeObject from '../api/project_data/getLargeObject.ts';

export type UseProjectDataResponse = {
    projectData: EntityDataModel;
    manipulation: EntityManipulationMethods;
    isLoading: boolean;
    isError: boolean;
    showReconnectionModal: boolean;
    closeReconnectionModal: () => void;
    reinitialize: () => void;
    playerEvent: (player: PlayerEvent) => void;

    // TODO: Rename to sendReactionEvent
    reactionEvent: (reaction: ReactionEvent) => void;
};

export interface Player {
    eventType: string;
    userId: string;
    nickname: string;
    containerName: string;
}

export interface PlayerEvent extends Player {
    x?: number;
    y?: number;
}

export interface ReactionEvent {
    animationType: 'float' | 'drop' | 'fly';
    message: string | null;
    color: string | null;
    emoji: string | null;
    left: number | null;
    top: number | null;
}

type UseProjectDataParams = {
    projectId: string;
    dataIndex: ProjectDataIndex;
    onRemoteManipulation?: EntityManipulationMethods;
    onInitialDataFetched?: (initialData: EntityDataModel) => void;
    onRemotePlayerEvent?: (player: PlayerEvent) => void;
    onRemoteReactionEvent?: (reaction: ReactionEvent) => void;
};

export default function useProjectData({
    projectId,
    dataIndex,
    onRemoteManipulation,
    onInitialDataFetched,
    onRemotePlayerEvent,
    onRemoteReactionEvent
}: UseProjectDataParams): UseProjectDataResponse {
    const {getAccessToken} = useAuthStateManager();
    const {apiServiceBaseUri} = useClientConfig();

    const mergeLargeObject = useMergeLargeObject();
    const mergeLargeObjectMutation = useMutation(mergeLargeObject);

    const onRemoteMergeData = onRemoteManipulation?.mergeData ?? undefined;
    const onRemoteRemoveElement =
        onRemoteManipulation?.removeElement ?? undefined;

    const fetchProductData = useFetchProjectData(projectId, dataIndex);
    const {
        data: initialData,
        isError,
        isLoading
    } = useQuery(['projectData', projectId, dataIndex], fetchProductData);

    const [entityData, setEntityData] = useState<EntityDataModel>({});

    const manipulation = useMemo(
        () => createEntityManipulationMethods(setEntityData),
        [setEntityData]
    );
    const {mergeData, removeElement} = manipulation;

    /*
     * Only when the data itself changes. The callback is a fresh lambda on
     * every render of the caller, and with it as a dependency this ran on
     * every render, resetting the state (and the canvas's idea of the board)
     * to the page-load snapshot.
     */
    const onInitialDataFetchedRef = useRef(onInitialDataFetched);
    onInitialDataFetchedRef.current = onInitialDataFetched;

    useEffect(() => {
        if (initialData) {
            setEntityData(initialData);
            onInitialDataFetchedRef.current?.(initialData);
        }
    }, [initialData]);

    const groupName = `${projectId}_${dataIndex}`;
    const [hubConnection, isHubDisconnected, reinitializeHubConnection] =
        useProjectDataHub(groupName);

    const [showReconnectionModal, setShowReconnectionModal] = useState(false);

    useEffect(() => {
        setShowReconnectionModal(isHubDisconnected);
    }, [isHubDisconnected]);

    useHubMessageListener(
        hubConnection,
        ProjectDataHubMessages.UpdatePlayerOnClient,
        (args: unknown) => {
            const playerEvent = args as PlayerEvent;

            if (onRemotePlayerEvent) {
                onRemotePlayerEvent(playerEvent);
            }
        },
        [groupName, hubConnection.connectionId, onRemotePlayerEvent]
    );

    useHubMessageListener(
        hubConnection,
        ProjectDataHubMessages.UpdateReactionOnClient,
        (args: unknown) => {
            const reactionEvent = args as ReactionEvent;

            if (onRemoteReactionEvent) {
                onRemoteReactionEvent(reactionEvent);
            }
        },
        [groupName, hubConnection.connectionId, onRemoteReactionEvent]
    );

    useHubMessageListener(
        hubConnection,
        ProjectDataHubMessages.UpdateProjectDataOnClient,
        (args: unknown) => {
            const payload = args as EntityDataPayload;

            switch (payload.op) {
                case EntityDataOperation.MergeData:
                    {
                        const data = payload.data!;

                        const {
                            largeObject,
                            projectId,
                            dataIndex,
                            elementId,
                            propNames
                        } = data;

                        if (typeof largeObject === 'boolean' && largeObject) {
                            console.log(
                                'UpdateProjectDataOnClient isLargeObject'
                            );
                            getLargeObject(
                                projectId as string,
                                dataIndex as number,
                                elementId as string,
                                propNames as string[],
                                apiServiceBaseUri,
                                getAccessToken
                            )
                                .then(fetchedElementData => {
                                    handleMergeData(
                                        payload.elementId,
                                        fetchedElementData,
                                        payload.meta
                                    );
                                })
                                .catch(error => {
                                    console.error(
                                        'Error fetching large object:',
                                        error
                                    );
                                });
                        } else {
                            handleMergeData(
                                payload.elementId,
                                data,
                                payload.meta
                            );
                        }
                    }
                    break;
                case EntityDataOperation.RemoveElement:
                    onRemoteRemoveElement &&
                        onRemoteRemoveElement(payload.elementId, payload.meta);
                    removeElement(payload.elementId);
                    break;
            }
        },
        [
            groupName,
            hubConnection.connectionId,
            onRemoteMergeData,
            onRemoteRemoveElement
        ]
    );

    function handleMergeData(
        elementId: string,
        data: UnknownObject,
        meta?: string | null | undefined
    ) {
        onRemoteMergeData && onRemoteMergeData(elementId, data, meta);
        mergeData(elementId, data);
    }

    const mergeDataAndBroadcast = (
        elementId: string,
        data: UnknownObject,
        meta?: string | null
    ) => {
        mergeData(elementId, data);

        const payload: EntityDataPayload = {
            projectId,
            dataIndex,
            elementId,
            data,
            op: EntityDataOperation.MergeData,
            meta
        };

        if (isLargeObject(payload, 16 * 1024 /* 16KB */)) {
            console.log('isLargeObject');
            mergeLargeObjectMutation.mutate(payload);
        } else {
            if (hubConnection.state === HubConnectionState.Connected) {
                hubConnection.invoke(
                    ProjectDataHubMessages.UpdateProjectDataOnServer,
                    groupName,
                    payload
                );
            } else {
                console.warn(
                    "SignalR connection is not in the 'Connected' state. Cannot send data."
                );
            }
        }
    };

    const removeElementAndBroadcast = (
        elementId: string,
        meta?: string | null
    ) => {
        removeElement(elementId);

        const payload: EntityDataPayload = {
            projectId,
            dataIndex,
            elementId,
            op: EntityDataOperation.RemoveElement,
            meta
        };

        if (hubConnection.state === HubConnectionState.Connected) {
            hubConnection.invoke(
                ProjectDataHubMessages.UpdateProjectDataOnServer,
                groupName,
                payload
            );
        } else {
            console.warn(
                "SignalR connection is not in the 'Connected' state. Cannot send data."
            );
        }
    };

    const playerEvent = (playerEvent: PlayerEvent) => {
        if (hubConnection.state === HubConnectionState.Connected) {
            hubConnection.invoke(
                ProjectDataHubMessages.UpdatePlayerOnServer,
                groupName,
                playerEvent
            );
        } else {
            console.warn(
                "SignalR connection is not in the 'Connected' state. Cannot send data."
            );
        }
    };

    const reactionEvent = (reactionEvent: ReactionEvent) => {
        if (hubConnection.state === HubConnectionState.Connected) {
            hubConnection.invoke(
                ProjectDataHubMessages.UpdateReactionOnServer,
                groupName,
                reactionEvent
            );
        } else {
            console.warn(
                "SignalR connection is not in the 'Connected' state. Cannot send data."
            );
        }
    };

    /*
     * Editors keep these functions from their first render (the whiteboard
     * hands them to CanvasController once). Each render builds them over that
     * render's hub connection, and the connection can be replaced (the
     * reconnect button, a new sign-in), so a kept copy would go on sending to
     * the old, stopped connection: every edit after that was dropped with a
     * console warning while the board looked fine. What goes out never
     * changes and always calls this render's versions.
     */
    const latest = useRef({
        mergeDataAndBroadcast,
        removeElementAndBroadcast,
        playerEvent,
        reactionEvent
    });
    latest.current = {
        mergeDataAndBroadcast,
        removeElementAndBroadcast,
        playerEvent,
        reactionEvent
    };

    const stable = useMemo(
        () => ({
            manipulation: {
                mergeData: (
                    elementId: string,
                    data: UnknownObject,
                    meta?: string | null
                ) =>
                    latest.current.mergeDataAndBroadcast(elementId, data, meta),
                removeElement: (elementId: string, meta?: string | null) =>
                    latest.current.removeElementAndBroadcast(elementId, meta)
            },
            playerEvent: (event: PlayerEvent) =>
                latest.current.playerEvent(event),
            reactionEvent: (event: ReactionEvent) =>
                latest.current.reactionEvent(event)
        }),
        []
    );

    return {
        projectData: entityData,
        manipulation: stable.manipulation,
        playerEvent: stable.playerEvent,
        reactionEvent: stable.reactionEvent,
        isLoading,
        isError,
        showReconnectionModal,
        reinitialize: reinitializeHubConnection,
        closeReconnectionModal: () => setShowReconnectionModal(false)
    };
}
